// DESIGN-SPEC §5.16 ImagePlaceholder 素材占位
import Image from "next/image";
import type { CSSProperties } from "react";
import { cn } from "@/lib/cn";

export type ImageRatio = "1:1" | "16:9" | "3:2" | "2:3" | "3:4" | "4:5" | "9:16";

export interface ImagePlaceholderProps {
  label: string;
  ratio: ImageRatio;
  className?: string;
  src?: string;
  alt?: string;
  sizes?: string;
  imageClassName?: string;
  badge?: string;
  objectPosition?: string;
  preload?: boolean;
}

const ratioClasses: Record<ImageRatio, string> = {
  "1:1": "aspect-square",
  "16:9": "aspect-video",
  "3:2": "aspect-[3/2]",
  "2:3": "aspect-[2/3]",
  "3:4": "aspect-[3/4]",
  "4:5": "aspect-[4/5]",
  "9:16": "aspect-[9/16]",
};

const placeholderBackground: CSSProperties = {
  backgroundImage:
    "linear-gradient(135deg, var(--c-forest-100), var(--c-tea-bg))",
};

const noiseBackground: CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(45deg, transparent 0 3px, color-mix(in srgb, var(--c-ink) 1.5%, transparent) 3px 6px)",
};

export function ImagePlaceholder({
  label,
  ratio,
  className,
  src,
  alt,
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 60vw, 720px",
  imageClassName,
  badge,
  objectPosition,
  preload = false,
}: ImagePlaceholderProps) {
  const normalizedLabel = label.replace(/^素材位[：:]\s*/, "");

  return (
    <div
      role={src ? undefined : "img"}
      aria-label={src ? undefined : `素材位：${normalizedLabel}，比例 ${ratio}`}
      className={cn(
        "relative overflow-hidden border border-line bg-forest-100",
        ratioClasses[ratio],
        className,
      )}
      style={src ? undefined : placeholderBackground}
    >
      {src ? (
        <>
          <Image
            src={src}
            alt={alt ?? normalizedLabel}
            fill
            sizes={sizes}
            preload={preload}
            className={cn("object-cover", imageClassName)}
            style={objectPosition ? { objectPosition } : undefined}
          />
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 bg-linear-to-t from-forest-900/30 via-transparent to-paper-inverse/5"
          />
          {badge ? (
            <span className="absolute right-2 bottom-2 border border-paper-inverse/25 bg-forest-900/72 px-2 py-1 font-mono text-[9px] leading-3 tracking-[0.12em] text-paper-inverse backdrop-blur-[2px]">
              {badge}
            </span>
          ) : null}
        </>
      ) : (
        <>
          <span
            aria-hidden="true"
            className="absolute inset-0"
            style={noiseBackground}
          />
          <span className="absolute inset-0 flex flex-col items-center justify-center px-5 text-center">
            <span className="font-mono text-[9px] leading-4 tracking-[0.2em] text-forest-500">ARCHIVE / PENDING</span>
            <span className="mt-2 font-display text-sm leading-6 font-semibold text-ink-2">{normalizedLabel}</span>
          </span>
        </>
      )}
    </div>
  );
}
